import { useState, useRef, useCallback } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { projects, contact } from '../data/portfolio';
import { ProjectCard } from '../components/FeaturedWork';
import { Motif, TextileDivider } from '../components/Ornaments';
import { Reveal } from '../components/Motion';
import { Button } from '../components/ui/button';

const TOOLTIP_OFFSET_X = 18;
const TOOLTIP_OFFSET_Y = 110;
const GRID_HIDDEN = { opacity: 0 };
const GRID_SHOWN = { opacity: 1 };
const GRID_TRANSITION = { duration: 0.3 };
const FILTERS = ['All work', 'Branding', 'Campaigns', 'Packaging', 'Photography'];

export default function WorkPage() {
  const [filter, setFilter] = useState('All work');
  const reduced = useReducedMotion();
  const visible = filter === 'All work' ? projects : projects.filter(p => p.type === filter);

  const tooltipRef = useRef(null);
  const [tooltipSrc, setTooltipSrc] = useState('');
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const positionTooltip = useCallback((e) => {
    if (!tooltipRef.current) return;
    tooltipRef.current.style.left = `${e.clientX + TOOLTIP_OFFSET_X}px`;
    tooltipRef.current.style.top = `${e.clientY - TOOLTIP_OFFSET_Y}px`;
  }, []);

  const onTitleEnter = useCallback((imageSrc, e) => {
    setTooltipSrc(imageSrc);
    setTooltipVisible(true);
    positionTooltip(e);
  }, [positionTooltip]);

  const onTitleMove = positionTooltip;

  const onTitleLeave = useCallback(() => {
    setTooltipVisible(false);
  }, []);

  return <main id="main-content" className="work-page" data-testid="work-page">
    <section className="archive-header page-inset">
      <Reveal><span className="eyebrow chapter-label" data-testid="archive-label">THE PROJECT ARCHIVE / 01–{String(projects.length).padStart(2, '0')}</span><h1 data-testid="work-page-heading">IDEAS IN<br />THE WILD<span className="terra-period">.</span></h1><p data-testid="work-page-intro">A mix of client work, collaborations and things I made because I couldn't not make them. Different briefs. Different mediums. The same curiosity.</p></Reveal>
      <div className="archive-side-note"><Motif /><p data-testid="archive-note">THOUGHTFULLY MADE.<br />OUT IN THE WORLD.</p></div>
    </section>
    <div className="archive-filter-row page-inset"><div className="archive-filters" role="group" aria-label="Filter projects by discipline">{FILTERS.map(type => <Button key={type} variant="ghost" className={filter === type ? 'filter-button selected' : 'filter-button'} aria-pressed={filter === type} data-testid={`filter-${type.toLowerCase().replace(' ', '-')}`} onClick={() => setFilter(type)}>{type}<span>{String(type === 'All work' ? projects.length : projects.filter(p => p.type === type).length).padStart(2, '0')}</span></Button>)}</div><span className="eyebrow archive-count" aria-live="polite" data-testid="project-count">{visible.length} PROJECT{visible.length !== 1 ? 'S' : ''}</span></div>
    <motion.div className="archive-grid" key={filter} initial={reduced ? false : GRID_HIDDEN} animate={GRID_SHOWN} transition={GRID_TRANSITION} data-testid="project-archive">
      {visible.map(project => <ProjectCard project={project} instance="archive" detailed key={project.id} onTitleEnter={onTitleEnter} onTitleMove={onTitleMove} onTitleLeave={onTitleLeave} />)}
    </motion.div>
    <div className="archive-end page-inset"><p data-testid="archive-end-note">There's always something else in the making.</p><a className="text-link" href={contact.behance} target="_blank" rel="noopener noreferrer" data-testid="archive-behance">MORE ON BEHANCE <span>↗</span></a></div><TextileDivider />
    <div ref={tooltipRef} className={`project-preview-tooltip ${tooltipVisible ? 'visible' : ''}`} data-testid="project-preview-tooltip">
      {tooltipSrc && <img src={tooltipSrc} alt="" />}
    </div>
  </main>;
}
